/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BackHandler,
  FlatList,
  View,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
} from "react-native";
import SearchIcon from "@assets/svg/SearchIcon";
import { debounce } from "lodash";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import { useFocusEffect } from "@react-navigation/native";
import color from "@constants/color";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import {
  getReconHistoryByDbId,
  markItemLostDamaged,
  markReconSubmit,
  submitReconCollectedCash,
} from "@services/api";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import CashCollection from "./components/CashCollection";
import ItemCollection from "./components/ItemCollection";
import ReconTabBtn from "./components/ReconTabBtn";
import ReconError from "./components/ReconError";
import ReconUserCard from "./components/ReconUserCard";
import CashCollectionModal from "./components/CashCollectionModal";
import BottomButton from "./components/BottomButton";
import { getPast10Dates } from "./utils";

const tabEnum = {
  PENDING: "pending",
  RTO: "rto",
};

const ReconHistoryDetails = ({ navigation, route }) => {
  const { item } = route.params;
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cashCollectionModalVisible, setCashCollectionModalVisible] =
    useState(false);
  const [reconData, setReconData] = useState({});
  const [currentExpandedIndex, setCurrentExpandedIndex] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tabEnum.RTO);
  const [selectedDate, setSelectedDate] = useState(
    item?.ofd_dates[item?.ofd_dates.length - 1],
  );
  const recondates = getPast10Dates({
    currentDate: new Date(),
  });
  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, selectedDate]),
  );
  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };
    const backHandlerSub = BackHandler.addEventListener("hardwareBackPress", backHandler);
    return () => {
      backHandlerSub.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };
  const fetchData = async ({ searchTerm = "", isSearch = true } = {}) => {
    try {
      setIsLoading(true);
      const { data } = await getReconHistoryByDbId({
        db_id: item?.db_id,
        ofd_date: selectedDate,
        search_term: isSearch ? searchTerm || searchText : "",
      });

      setReconData(data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitReconCollectedCash = async (amount) => {
    try {
      setIsLoading(true);
      const payload = {
        ofd_date: reconData?.ofdDate,
        amount,
        totalCollectedFromCx: reconData?.cashCollectedFromCx,
      };
      await submitReconCollectedCash({
        db_id: item?.db_id,
        payload,
      });
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitReconItemMissing = async (data, value) => {
    try {
      setIsLoading(true);
      const payload = {
        ofd_date: reconData?.ofdDate,
        lost_damaged: value,
        order_item_id: data?.id,
        actual_quantity: data?.quantity,
        prev_missing_quantity: data?.missing_qty,
        source: "RECON_HISTORY",
        cp_db_id: item?.db_id,
      };

      await markItemLostDamaged(payload);
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSearchInputChange = debounce((text) => {
    setSearchText(text);
    fetchData({ searchTerm: text, isSearch: text.length > 0 });
  }, 500);

  const setReconTab = (tab) => {
    setSelectedTab(tab);
  };
  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setIsRefreshing(false);
  };

  const isDateDisabled = (date) => {
    return !item?.ofd_dates.includes(date);
  };
  const flatListRef = useRef(null);

  const scrollLeft = () => {
    flatListRef.current.scrollToOffset({
      offset: 0,
      animated: true,
    });
  };

  const scrollRight = () => {
    flatListRef.current.scrollToEnd({ animated: true });
  };

  const WeekView = ({ data }) => {
    return (
      <View style={{ marginBottom: 4 }}>
        <Text variant="title_16">Timeline</Text>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <TouchableOpacity
            testID="rhd-scroll-btn"
            onPress={scrollRight}
            style={{ left: -10, position: "absolute" }}
          >
            <ChevronRight
              style={{
                height: 12,
                width: 10,
                transform: [{ rotate: "180deg" }],
              }}
            />
          </TouchableOpacity>
          <FlatList
            horizontal
            data={data.reverse()}
            ref={flatListRef}
            inverted
            initialScrollIndex={data?.findIndex((i) => i.date === selectedDate)}
            getItemLayout={(data, index) => ({
              length: 50,
              offset: 50 * index,
              index,
            })}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.date}
            renderItem={({ item }) => (
              <TouchableOpacity
                testID="rhd-btn"
                activeOpacity={0.8}
                disabled={isDateDisabled(item.date)}
                style={[
                  styles.item,
                  item.date === selectedDate && {
                    backgroundColor: color.shadowLighter,
                    borderColor: color.black,
                  },
                ]}
                onPress={() => setSelectedDate(item.date)}
              >
                <Text
                  variant={
                    item.date === selectedDate ? "caption_1" : "caption_4"
                  }
                  style={styles.day}
                >
                  {item.day.charAt(0)}
                </Text>
                <Text
                  variant="body_text_4"
                  style={[
                    styles.date,
                    isDateDisabled(item.date) && {
                      color: color.lightMediumBlack,
                    },
                  ]}
                >
                  {item.date.slice(-2)}
                </Text>
              </TouchableOpacity>
            )}
          />
          <TouchableOpacity
            testID="rhd-scroll-btn"
            onPress={scrollLeft}
            style={{ right: -10, position: "absolute" }}
          >
            <ChevronRight style={{ height: 12, width: 10 }} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderheader = () => {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: color.white,
          padding: 16,
          paddingVertical: 0,
          paddingBottom: 16,
          marginBottom: 8,
        }}
      >
        <WeekView data={recondates} />
        <CashCollection
          totalCollectedFromCx={reconData?.cashCollectedFromCx}
          onPress={() => setCashCollectionModalVisible(true)}
          totalCollectedFromDb={reconData?.totalCollectedFromDb}
          onPressSubmit={onSubmitReconCollectedCash}
          reconEditingEnabled={reconData?.reconEditingEnabled}
          showDivinder={true}
        />
        <ItemCollection
          totalItemCollectionAmount={reconData?.totalItemCollectionAmount}
          totalItemCollectionQty={reconData?.totalItemCollectionQty}
        />
        <ReconTabBtn
          pending={reconData?.openItems?.length}
          rto={reconData?.returnedItems?.length}
          selectedTab={selectedTab}
          setSelectedTab={setReconTab}
        />
        <ReconError
          text="Lost/Damage marked by DB after Out For Delivery: "
          value={reconData?.totalIssuesMarked}
        />
      </View>
    );
  };
  const callCustomer = (phoneNumber) => {
    handleCallButtonPress(phoneNumber);
  };

  const renderItem = ({ item, index }) => {
    return (
      <ReconUserCard
        item={item}
        callCustomer={callCustomer}
        onPressMissingQty={onSubmitReconItemMissing}
        setCurrentExpandedIndex={setCurrentExpandedIndex}
        index={index}
        expandedIndex={currentExpandedIndex}
      />
    );
  };
  const onPressBottomButton = async () => {
    setIsLoading(true);
    try {
      const payload = {
        ofd_date: reconData?.ofdDate,
      };
      await markReconSubmit({
        db_id: item?.db_id,
        payload,
      });
      navigation.pop();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <HeaderWithSearch
        headertitle="History"
        headerColor="#fff"
        hideShadow={true}
        navigation={navigation}
        route={route}
        searchPlaceholder="Search customer, route, items, bag no."
        isSearchEnabled={isSearchEnabled}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        searchIcon={<SearchIcon height={24} width={24} />}
        onSearchInputChange={onSearchInputChange}
        onPressCrossIcon={() => onSearchInputChange("")}
        onPressSearchIcon={() => setIsSearchEnabled(true)}
      />
      {isLoading ? (
        <ActivityIndicator
          size="large"
          color={color.primary}
          style={styles.loading}
        />
      ) : (
        <>
          <FlatList
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            data={
              selectedTab === tabEnum.RTO
                ? reconData?.returnedItems
                : reconData?.openItems
            }
            keyExtractor={(i) => i.user_id}
            renderItem={renderItem}
            ListHeaderComponent={isSearchEnabled ? null : renderheader}
            ListFooterComponent={<View style={{ height: 120 }} />}
          />
          <BottomButton
            title="Continue"
            onPress={onPressBottomButton}
            totalMissingAmount={reconData?.totalMissingAmount}
            totalMissingQty={reconData?.totalMissingQty}
          />
          <CashCollectionModal
            showModal={cashCollectionModalVisible}
            setShowModal={setCashCollectionModalVisible}
            totalCollectedFromCxSummary={reconData?.totalCollectedFromCxSummary}
          />
        </>
      )}
    </View>
  );
};

export default ReconHistoryDetails;

const styles = StyleSheet.create({
  loading: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  item: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    paddingVertical: 8,
    borderWidth: 1,
    margin: 4,
    marginVertical: 8,
    borderRadius: 30,
    borderColor: color.lighterBlack,
  },
  day: {
    color: color.lightMediumBlack,
  },
  date: {
    color: color.black,
  },
});
